import { useEffect, useMemo, useState } from "react";
import type { Kundenartikel, KundenartikelList } from "@bidi-handel/core/schemas";
import { api, ApiError } from "../../api/client";
import { Icon } from "../../shell/Icon";

/**
 * Modal zur manuellen Artikel-Auswahl im Redesign-Look.
 * Funktional unverändert: lädt Kundenartikel, filtert clientseitig,
 * meldet Pick zurück via onPicked.
 */

export function ArticlePickerDialog({
  tenantId,
  positionId,
  positionOz,
  originaltext,
  lvEinheit,
  lvMenge,
  currentArtikelCode,
  onClose,
  onPicked,
}: {
  tenantId: string;
  positionId: string;
  positionOz: string;
  originaltext: string;
  lvEinheit: string;
  lvMenge: number;
  currentArtikelCode: string | null;
  onClose: () => void;
  onPicked: (artikelCode: string | null) => Promise<void>;
}) {
  const [list, setList] = useState<KundenartikelList | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .getKundenartikel(tenantId)
      .then((data) => {
        if (!cancelled) setList(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // Escape to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    if (!list) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return list;
    return list.filter((a) => matchesQuery(a, q));
  }, [list, filter]);

  async function handlePick(artikelCode: string | null) {
    setSubmitError(null);
    setSubmitting(true);
    try {
      await onPicked(artikelCode);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { hint?: string; error?: string } | null;
        setSubmitError(body?.hint ?? body?.error ?? err.message);
      } else if (err instanceof Error) setSubmitError(err.message);
      else setSubmitError(String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 820 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <div className="uppercase-mini">/ MATCH / OVERRIDE</div>
            <div className="modal-title">Artikel manuell wählen</div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="pick-target">
            <div className="uppercase-mini" style={{ color: "var(--fg-dim)" }}>
              FÜR POSITION
            </div>
            <div className="pick-pos mono">
              {positionOz} · {positionId}
            </div>
            <div className="pick-meta mono dim">
              {originaltext} — Menge: <span className="mono">{lvMenge}</span> {lvEinheit}
            </div>
          </div>

          <div className="search-wrap" style={{ width: "100%", marginBottom: 14 }}>
            <span className="search-ico">
              <Icon name="search" size={15} />
            </span>
            <input
              autoFocus
              type="text"
              className="input search-input"
              placeholder="Artikel-Code / Bezeichnung / Warengruppe / Suchbegriff …"
              value={filter}
              onChange={(e) => setFilter(e.currentTarget.value)}
            />
            <span className="mono search-kbd">↵</span>
          </div>

          <p
            className="mono"
            style={{ fontSize: 11, color: "var(--fg-faint)", marginBottom: 10 }}
          >
            {list ? `${filtered.length} / ${list.length} Artikel` : "Lade Stammdaten …"}
          </p>

          {loadError && (
            <div
              className="warn-line"
              style={{
                borderLeftColor: "var(--bad)",
                background: "oklch(0.72 0.18 25 / 0.08)",
              }}
            >
              <strong>Stammdaten nicht ladbar:</strong>{" "}
              <span className="mono">{loadError}</span>
            </div>
          )}

          {!loadError && list && filtered.length === 0 && (
            <div className="dim" style={{ padding: "16px 0" }}>
              Kein Artikel entspricht der Suche.
            </div>
          )}

          {!loadError && filtered.length > 0 && (
            <div className="pick-list">
              {filtered.map((a, i) => {
                const isCurrent = a.code === currentArtikelCode;
                const einheitMismatch =
                  a.einheit && a.einheit.toLowerCase() !== lvEinheit.toLowerCase();
                return (
                  <button
                    key={a.code}
                    type="button"
                    className="pick-row"
                    style={{
                      animationDelay: `${i * 22}ms`,
                      borderColor: isCurrent ? "oklch(0.78 0.16 152 / 0.5)" : undefined,
                      background: isCurrent
                        ? "linear-gradient(90deg, var(--ok-tint), var(--bg-base) 60%)"
                        : undefined,
                    }}
                    disabled={submitting || isCurrent}
                    onClick={() => handlePick(a.code)}
                  >
                    <div>
                      <div className="pick-head">
                        <span className="mono pick-code">{a.code}</span>
                        {a.warengruppe && (
                          <span className="pill" style={{ padding: "2px 8px" }}>
                            <span className="mono">{a.warengruppe}</span>
                          </span>
                        )}
                        {a.einheit && (
                          <span className="mono pick-einh">{a.einheit}</span>
                        )}
                        {einheitMismatch && (
                          <span className="pill warn" style={{ fontSize: 10 }}>
                            Einheit: {a.einheit} ≠ {lvEinheit}
                          </span>
                        )}
                        {isCurrent && (
                          <span className="pill ok" style={{ fontSize: 10 }}>
                            <span className="dot" />
                            AKTIV
                          </span>
                        )}
                      </div>
                      <div className="pick-bez">{a.bezeichnung}</div>
                      {a.kurzText && a.kurzText !== a.bezeichnung && (
                        <div className="pick-bez dim" style={{ marginTop: 2 }}>
                          {a.kurzText}
                        </div>
                      )}
                    </div>
                    <div className="pick-r">
                      {typeof a.listenpreis === "number" && (
                        <div className="pick-vk mono">
                          {a.listenpreis.toFixed(2)} €
                          {a.einheit ? (
                            <span className="dim" style={{ fontSize: 10 }}>
                              {" "}
                              / {a.einheit}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {submitError && (
            <div
              className="warn-line"
              style={{
                marginTop: 14,
                borderLeftColor: "var(--bad)",
                background: "oklch(0.72 0.18 25 / 0.08)",
              }}
            >
              <strong>Zuordnung fehlgeschlagen:</strong>{" "}
              <span className="mono">{submitError}</span>
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn ghost" onClick={onClose} disabled={submitting}>
            Abbrechen
          </button>
          <div style={{ flex: 1 }} />
          {currentArtikelCode && (
            <button
              type="button"
              className="btn"
              disabled={submitting}
              onClick={() => handlePick(null)}
            >
              Auswahl entfernen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function matchesQuery(a: Kundenartikel, q: string): boolean {
  if (a.code.toLowerCase().includes(q)) return true;
  if (a.bezeichnung.toLowerCase().includes(q)) return true;
  if (a.kurzText?.toLowerCase().includes(q)) return true;
  if (a.warengruppe?.toLowerCase().includes(q)) return true;
  if (a.suchbegriffe.some((s) => s.toLowerCase().includes(q))) return true;
  return false;
}
